import React from "react";

import s from "./ui.module.scss";

export interface Column<T> {
  /** Column header label. */
  header: React.ReactNode;
  /** Cell renderer for a row. */
  cell: (row: T, index: number) => React.ReactNode;
  align?: "left" | "right";
  width?: string | number;
}

export interface DataTablePagination {
  page: number; // 1-based
  pageCount: number;
  total?: number;
  onPage: (page: number) => void;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  empty?: React.ReactNode;
  pagination?: DataTablePagination;
  /** Makes rows clickable (e.g. navigate to a detail page). */
  onRowClick?: (row: T, index: number) => void;
  /**
   * Right-align the HEADER of every `align: "right"` column, not just its cells.
   * Without it a money column's label sits at the left edge while its figures sit
   * at the right, which on a wide table means counting columns to match a heading
   * to a number.
   *
   * Off by default on purpose: `align: "right"` has 83 call sites across 73 files
   * here, so this is opted into by the tables that have been looked at rather than
   * turned on for all of them at once. See `.alignedHeaders` in ui.module.scss for
   * why that rule is not the redundant duplicate it looks like.
   */
  alignHeaders?: boolean;
}

// Compact page-number list with ellipses (1 … n-1 n).
const pageList = (page: number, pageCount: number): (number | "…")[] => {
  if (pageCount <= 5) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  if (page > 3) out.push("…");
  for (let p = Math.max(2, page - 1); p <= Math.min(pageCount - 1, page + 1); p++) out.push(p);
  if (page < pageCount - 2) out.push("…");
  out.push(pageCount);
  return out;
};

function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  pagination,
  onRowClick,
  alignHeaders,
}: DataTableProps<T>) {
  return (
    <>
      <div className={s.tableWrap}>
        <table className={alignHeaders ? `${s.table} ${s.alignedHeaders}` : s.table}>
          <thead>
            <tr>
              {columns.map((c, i) => (
                <th key={i} className={c.align === "right" ? s.cellRight : undefined} style={c.width ? { width: c.width } : undefined}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length}>
                  <div className={s.empty}>{empty ?? "No results"}</div>
                </td>
              </tr>
            ) : (
              rows.map((row, ri) => (
                <tr
                  key={rowKey(row, ri)}
                  className={onRowClick ? s.rowClickable : undefined}
                  onClick={onRowClick ? () => onRowClick(row, ri) : undefined}
                >
                  {columns.map((c, ci) => (
                    <td key={ci} className={c.align === "right" ? s.cellRight : undefined}>
                      {c.cell(row, ri)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.pageCount > 1 && (
        <div className={s.pagination}>
          <span>
            {pagination.total != null
              ? `${rows.length} of ${pagination.total.toLocaleString()} results`
              : `Page ${pagination.page} of ${pagination.pageCount}`}
          </span>
          <div className={s.pages}>
            <button
              type="button"
              className={s.pageBtn}
              disabled={pagination.page <= 1}
              onClick={() => pagination.onPage(pagination.page - 1)}
              aria-label="Previous page"
            >
              ‹
            </button>
            {pageList(pagination.page, pagination.pageCount).map((p, i) =>
              p === "…" ? (
                <span key={`e${i}`} className={s.pageBtn} aria-hidden="true">
                  …
                </span>
              ) : (
                <button
                  type="button"
                  key={p}
                  className={`${s.pageBtn}${p === pagination.page ? ` ${s.pageBtnActive}` : ""}`}
                  onClick={() => pagination.onPage(p)}
                >
                  {p}
                </button>
              )
            )}
            <button
              type="button"
              className={s.pageBtn}
              disabled={pagination.page >= pagination.pageCount}
              onClick={() => pagination.onPage(pagination.page + 1)}
              aria-label="Next page"
            >
              ›
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default DataTable;
